/**
 * A render fault held to one part of the page (2026-10-30). The app has no boundary above its routes, so an error thrown
 * while one part draws unmounts the whole page and leaves it blank: one widget with odd data blanked a board. This
 * boundary draws `fallback` in that part's place instead, and draws the part again when `resetKey` changes (an edit to
 * what it draws) or when the fallback calls `reset`.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

export interface ErrorBoundaryProps {
  /** What draws in the part's place: the error, and a reset that draws the part again. */
  fallback: (error: Error, reset: () => void) => ReactNode;
  /** A new value draws the part again: say, a widget's settings after an edit. */
  resetKey?: string;
  /** For the console: which part failed. */
  label?: string;
  children?: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  resetKey?: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(thrown: unknown): Partial<ErrorBoundaryState> {
    return { error: thrown instanceof Error ? thrown : new Error(String(thrown)) };
  }

  static getDerivedStateFromProps(props: ErrorBoundaryProps, state: ErrorBoundaryState): Partial<ErrorBoundaryState> | null {
    return props.resetKey === state.resetKey ? null : { error: null, resetKey: props.resetKey };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The stack stays in the console for diagnosis; the page stays up.
    console.error(`${this.props.label ?? "A part of the page"} could not be drawn:`, error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  render() {
    return this.state.error ? this.props.fallback(this.state.error, this.reset) : this.props.children;
  }
}
